import addEventCapabilities from "./event_capabilities_queued.js"
import ScreenCanvas from "./screen_canvas.js"
import addScreenShapes from "./screen_shapes.js"
import local2D from "./local_2D.js"
import topLeftAndPivot from "./topleft_and_pivot.js"
import ScreenImage from "./screen_image.js"
import deprecations from "./deprecation.js"
import numberUtils from "./number_utils.js"
import exportMethods from "./export_methods.js"

const {normalizedParamsForRectangle} = topLeftAndPivot

const {globalPointFromReferenceFrame} = local2D

const {roundToSignificantDigits} = numberUtils

function Screen (container, {crispCanvas, errorHandler, adapter: Adapter = ScreenCanvas} = {}) {

    //Support for jquery element
    if (Object.getPrototypeOf(container).jquery) {
        container = container[0]
    }

    this.canvas = container
    this.crispCanvas = crispCanvas

    this.drawAdapter = new Adapter(container, {screenParent: this})

    if (errorHandler) {
        this.errorHandler = errorHandler
        this.drawAdapter.errorHandler = errorHandler
    }

    this.internalEventBus = {}
    addEventCapabilities(this.internalEventBus)

    this.groupMaxDepth = 20

    this.cameraVersion = 0
    this.updateCanvasSize()
    this.yDir = 1
    this.setScale(1)
    this.center = {x: 0, y: 0}
    this.setOrientation(0)

    this.attachedElements = new Map()

}


ScreenImage.Screen = Screen //to enable toScreen export while avoiding circular deps.


addScreenShapes(Screen)


Screen.prototype.updateCanvasSize = function () { //eslint-disable-line complexity

    let {width, height} = this.drawAdapter.updatedDimensions()
    const {width: realWidth, height: realHeight} = this.canvas.getBoundingClientRect()


    let domScaleX = realWidth  / width
    let domScaleY = realHeight / height

    if (
        width === this.width && height === this.height &&
        domScaleX === this.domScaleX && domScaleY === this.domScaleY
    ) {
        return false
    }


    this.width          = width
    this.height         = height
    this.widthInPixels  = width
    this.heightInPixels = height

    this.domScaleX = domScaleX
    this.domScaleY = domScaleY



    this.centerPix = {
        x: width  / 2,
        y: height / 2
    }

    //FIXME
    if (this.drawAdapter.setCenterAdapterScreenParent) {
        this.centerAdapter = this.drawAdapter.setCenterAdapterScreenParent()
    } else {
        this.centerAdapter = this.centerPix
    }
    this.cameraVersion += 1

    this.internalEventBus.emit("resized")

    return true
}


function pixBoxFromParams (screen, params = {}) {
    let {topLeft, pixelZone} = params
    if (topLeft) { //FIXME Use topLeftAndPivot ?
        let {xMin, yMax, width, height} = screen.pixBoundingBox(params)
        return {x: xMin, y: yMax, width, height}
    } else if (pixelZone) {
        return pixelZone
    } else {
        return {
            x: 0,
            y: 0,
            width:  screen.width,
            height: screen.height
        }
    }
}


Screen.prototype.clear = function (params) {
    params = Object.assign({}, params)

    let {x, y, width, height} = pixBoxFromParams(this, params)
    params.x      = x
    params.y      = y
    params.width  = width
    params.height = height

    this.drawAdapter.clear(params)
}





//****************************** PIXEL AND POINT STATUS


Screen.prototype.isInside = function (point, referenceFrame) {
    return this.drawAdapter.isInside(this.coordsToPixelCoords(point, referenceFrame))
}


Screen.prototype.isOutside = function (point, referenceFrame) {
    return !this.isInside(point, referenceFrame)
}


Screen.prototype.getPixelColor = function (point, referenceFrame) {
    return this.drawAdapter.getPixelColor(this.coordsToPixelCoords(point, referenceFrame))
}






//****************************** TEXT TOOLS


const pxSyntaxRegexp = /((\d+\.\d+)|(\d+\.?)|(\.\d+))px\b/g

Screen.prototype.getTextDimensions = function (params) {
    let {width, height, font} = this.drawAdapter.getTextDimensions(Screen.cloneAndConvert(this, params))
    const {scale} = this

    width  = roundToSignificantDigits(width / scale, 4)
    height = roundToSignificantDigits(height / scale, 4)


    let result = {width, height}

    if (!params.font.includes("px")) {
        font = font.replace(pxSyntaxRegexp, function (match, p1) {
            const value = roundToSignificantDigits(parseFloat(p1, 10) / scale, 4)
            result.fontSize = value
            return `${value}u`
        })
    }

    result.font = font

    return result
}







//****************************** EXPORTS

Screen.prototype.toCanvas = function (params = {}) {
    params = Screen.cloneAndConvert(this, params)
    return this.drawAdapter.toCanvas(params)
}


// toImageData, toDataURL, toImageBitmap, toBlob, toBase64, toImageElement,
// toPNGImage and downloadAsImage, all over toCanvas. Unlike cloneAndConvert
// (which only keeps drawing params) these get the params as the user wrote
// them, so name / format / quality / type reach the download and the encoders.
exportMethods.attachExportMethods(Screen.prototype, {
    downloadName:  "downloadAsImage",
    defaultName:   "screen",
    messagePrefix: "screen.export"
})


// how log(screen) shows in terminals (rich_value.js)
Screen.prototype.terminalDisplay = function () {
    return Object.assign(this.drawAdapter.terminalDisplay(), {constructorName: "Screen"})
}

// the export region of a screen is expressed at the top level of the params
// (world coords / lengths, converted to canvas pixels by toCanvas), so the
// params themselves are the crop source of the new image
Screen.prototype.toScreenImage = function (params = {}) {
    const cropSource = Object.assign({}, params)
    return new ScreenImage(Object.assign({}, params, {source: this, cropSource}))
}


Screen.prototype.forToxiElement = function () {
    return this.toImageElement()
}



//****************************** CONVERSIONS

const degreesToRadians = Math.PI / 180


function coordsToPixelCoordsWithPixelMode (coords, referenceFrame) {
    if (referenceFrame) {
        return globalPointFromReferenceFrame(referenceFrame, coords.x, coords.y)
    } else {
        return coords
    }
}


function pixelCoordsToCoordsWithPixelMode (pix) {
    return pix
}


//pixels mean "canvas pixels", if canvas is stretched or rotated in css, those won't be "DOM pixels"
//see coordsToDomPix / domPixelCoordsToCoords
Screen.prototype.coordsToPixelCoords = function ({x, y}, referenceFrame) {
    if (referenceFrame) {
        ({x, y} = globalPointFromReferenceFrame(referenceFrame, x, y))
    }
    let tmpX = x - this.center.x
    let tmpY = y - this.center.y
    return {
        x: this.centerPix.x + this.scale * (tmpX * this.cosOrientation - tmpY * this.sinOrientation),
        y: this.centerPix.y - this.scale * (tmpX * this.sinOrientation + tmpY * this.cosOrientation)
    }
}


Screen.prototype.coordsToAdapterCoords = function ({x, y}, referenceFrame) {
    if (referenceFrame) {
        ({x, y} = globalPointFromReferenceFrame(referenceFrame, x, y))
    }
    let tmpX = x - this.center.x
    let tmpY = y - this.center.y
    return {
        x: this.centerAdapter.x + this.adapterScale  * (tmpX * this.cosOrientation - tmpY * this.sinOrientation),
        y: this.centerAdapter.y + this.adapterScaleY * (tmpX * this.sinOrientation + tmpY * this.cosOrientation)
    }
}


Screen.prototype.vectorCoordsToAdapterCoords = function ({x, y}, referenceFrame) {
    if (referenceFrame) {
        let o = globalPointFromReferenceFrame(referenceFrame, 0, 0)
        let v = globalPointFromReferenceFrame(referenceFrame, x, y)
        x = v.x - o.x
        y = v.y - o.y
    }
    return {
        x: this.adapterScale  * (x * this.cosOrientation - y * this.sinOrientation),
        y: this.adapterScaleY * (x * this.sinOrientation + y * this.cosOrientation)
    }
}




//see note on coordsToPixelCoords
Screen.prototype.pixelCoordsToCoords = function ({x, y}) {
    let tmpX = (x - this.centerPix.x) / this.scale
    let tmpY = (y - this.centerPix.y) / this.scale
    return {
        x: this.center.x + (tmpX * this.cosOrientation - tmpY * this.sinOrientation),
        y: this.center.y - (tmpX * this.sinOrientation + tmpY * this.cosOrientation)
    }
}


//pixels relative to page, not to canvas. See coordsToPixelCoords
//won't work with css transforms
Screen.prototype.coordsToDomPix = function ({x, y}, referenceFrame) {
    const {x: xPix, y: yPix} = this.coordsToPixelCoords({x, y}, referenceFrame)

    const {canvas, width, height} = this
    const widthRatio  = canvas.clientWidth  / width
    const heightRatio = canvas.clientHeight / height
    const {left, top} = canvas.getBoundingClientRect()

    return {
        x: left + xPix * widthRatio,
        y: top  + yPix * heightRatio
    }
}


Screen.prototype.domPixelCoordsToCoords = function ({x, y}) {
    const {canvas, width, height, domScaleX, domScaleY} = this
    const widthRatio  = width  / canvas.clientWidth
    const heightRatio = height / canvas.clientHeight
    const {left, top} = canvas.getBoundingClientRect()

    return this.pixelCoordsToCoords({
        x: (x + 0.5 - left) * widthRatio  / domScaleX,
        y: (y + 0.5 - top)  * heightRatio / domScaleY
    })
}


//get mouse pos relative to canvas (Firefox compatibilities)
Screen.prototype.mouseEventToCoords = function (event) {
    const {clientX: x, clientY: y} = event
    return this.domPixelCoordsToCoords({x, y})
}

Screen.prototype.mouseEventToSmartTrimmedCoords = function (event) {
    const {clientX: x, clientY: y} = event
    return this.smartTrimCoords(this.domPixelCoordsToCoords({x, y}))
}

Screen.prototype.touchEventToCoords = function (event) {
    const {clientX: x, clientY: y} = event.touches[0]
    return this.domPixelCoordsToCoords({x, y})
}

Screen.prototype.touchEventToSmartTrimmedCoords = function (event) {
    const {clientX: x, clientY: y} = event.touches[0]
    return this.smartTrimCoords(this.domPixelCoordsToCoords({x, y}))
}

Screen.prototype.eventToCoords = function (event) {
    if (event.touches) {
        return this.touchEventToCoords(event)
    } else {
        return this.mouseEventToCoords(event)
    }
}

Screen.prototype.eventToSmartTrimmedCoords = function (event) {
    if (event.touches) {
        return this.touchEventToSmartTrimmedCoords(event)
    } else {
        return this.mouseEventToSmartTrimmedCoords(event)
    }
}


Screen.prototype.smartTrimCoords = function ({x, y}) {
    const {smartTrimRatio} = this
    return {
        x: Math.round(x * smartTrimRatio) / smartTrimRatio,
        y: Math.round(y * smartTrimRatio) / smartTrimRatio
    }
}



deprecations.add("Screen", Screen.prototype, {
    mouseEvent2Coords:    "mouseEventToCoords",
    domPix2coords:        "domPixelCoordsToCoords",
    domPixToCoords:       "domPixelCoordsToCoords",
    coords2domPix:        "coordsToDomPix",
    pix2coords:           "pixelCoordsToCoords",
    pixToCoords:          "pixelCoordsToCoords",
    coords2pix:           "coordsToPixelCoords",
    coordsToPix:          "coordsToPixelCoords",
    setOptimalView:       "setOptimalCamera",
    optimalViewParamsFor: "optimalCameraParamsFor"
})






Screen.prototype.boxToPixBox = function (params) {
    let {topLeftPoint, width, height, orientation} = normalizedParamsForRectangle(params, this.yDir)

    if (orientation) {
        throw new Error("orientation not supported")
    }

    const {x, y} = this.coordsToPixelCoords(topLeftPoint)

    width  *= this.scale
    height *= this.scale

    return {x, y, width, height}
}


Screen.prototype.boxToDomStyle = function (params) {

    let {x, y, width, height} = this.boxToPixBox(params)

    return {
        position: "absolute",
        top:    `${y}px`,
        left:   `${x}px`,
        width:  `${width}px`,
        height: `${height}px`
    }
}


Screen.prototype.attachElement = function (params) {
    let {element} = params
    this.attachedElements.set(element, params)
    moveElement(this, params)
}


Screen.prototype.updateAttachedElements = function () {
    this.attachedElements.forEach(params => moveElement(this, params))
}


function moveElement (screen, params) {
    let {element}  = params
    let {$element} = element

    if ($element) {
        if (
            element.lastAttachedVersion === element.version &&
            element.lastAttachedCamera  === screen.cameraVersion
        ) {
            return
        }
        element.lastAttachedVersion = element.version
        element.lastAttachedCamera  = screen.cameraVersion
        element = $element
    }

    let widthSpecified  = ("width"  in params)
    let heightSpecified = ("height" in params)

    params = Object.assign({
        width:  element.outerWidth()  / screen.scale,
        height: element.outerHeight() / screen.scale
    }, params)

    const {x, y, width, height} = screen.boxToPixBox(params)

    if (widthSpecified) {
        element.outerWidth(width)
    }
    if (heightSpecified) {
        element.outerHeight(height)
    }
    element.css({
        position: "absolute",
        top:  `${y}px`,
        left: `${x}px`,
        margin: 0
    })

}







//****************************** SCREEN MOVEMENTS

// if we mention the keyword "Camera", user expect orientation to mean the camera orientation, not the screen orientation
// hence the negative sign on orientation

Screen.prototype.getCamera = function () {
    let {center, scale, orientation, width, height} = this
    let {x, y} = center
    return {
        center: {x, y},
        unitInPixels: scale,
        scale,                  //deprecated alias of unitInPixels
        width:  width  / scale,
        height: height / scale,
        orientation: (orientation === 0 ? 0 : -orientation)
    }
}


Screen.prototype.setCamera = function ({center, scale, orientation = false}) {
    if (scale) {
        this.setScale(scale)
    }
    if (center) {
        this.setCenter(center)
    }
    if (orientation !== false) {
        this.setOrientation(-orientation)
    }
}


Screen.prototype.setScale = function (scale) {
    if (this.crispCanvas) {
        scale *= 2
    }
    if (scale === this.scale) {
        return
    }
    this.cameraVersion += 1
    this.scale = scale

    //FIXME
    if (this.drawAdapter.setScaleForScreenParent) {
        this.drawAdapter.setScaleForScreenParent()
    } else {
        this.adapterScale  = scale
        this.adapterScaleY = -scale
    }
    this.smartTrimRatio = Math.pow(10, Math.ceil(Math.log10(scale)))
}


Screen.prototype.invertY = function (inversion) {
    this.yDir = inversion ? -1 : 1
    this.setScale(this.scale)
    this.sinOrientation = Math.sin(this.orientation * degreesToRadians) * this.yDir
}


Screen.prototype.multiplyScale = function (multiplier) {
    this.setScale(this.scale * multiplier)
}


Screen.prototype.setCenter = function ({x = this.center.x, y = this.center.y}) {
    if (x !== this.center.x || y !== this.center.y) {
        this.cameraVersion += 1
        this.center = {x, y}
    }
}


Screen.prototype.translateCenter = function ({x = 0, y = 0}) {
    this.cameraVersion += 1
    this.center.x += x
    this.center.y += y
}


Screen.prototype.setOrientation = function (orientationDeg) {
    if (orientationDeg === this.orientation) {
        return
    }
    this.cameraVersion += 1
    this.orientation   = orientationDeg
    let orientationRad = orientationDeg * degreesToRadians
    this.cosOrientation = Math.cos(orientationRad)
    this.sinOrientation = Math.sin(orientationRad) * this.yDir
}


Screen.prototype.rotate = function (angleDeg) {
    this.setOrientation(this.orientation + angleDeg)
}


Screen.prototype.usePixelCoordinates = function () {
    this.setCenter({x: 0, y: 0})
    this.setOrientation(0)
    this.invertY(true)
    this.coordsToPixelCoords = coordsToPixelCoordsWithPixelMode
    this.pixelCoordsToCoords = pixelCoordsToCoordsWithPixelMode
}



// zones : array of {x:, y:, margin:} (margin is optional)
Screen.prototype.optimalCameraParamsFor = function (zones, {
    minScale,
    maxScale,
    margin: globalMargin = 0,
    marginRatio,
    snap
} = {}) {

    let zonesPixBox = {
        xMin:  Infinity,
        xMax: -Infinity,
        yMin:  Infinity,
        yMax: -Infinity
    }

    zones.forEach(({x, y, margin = 0}) => {
        let pixZone    = this.coordsToPixelCoords({x, y})
        pixZone.margin = margin * this.scale
        addZoneToPixBox(zonesPixBox, pixZone)
    })

    if (globalMargin) {
        globalMargin *= this.scale
        zonesPixBox.xMin -= globalMargin
        zonesPixBox.xMax += globalMargin
        zonesPixBox.yMin -= globalMargin
        zonesPixBox.yMax += globalMargin
    }

    let pixCenter = {
        x: (zonesPixBox.xMin + zonesPixBox.xMax) / 2,
        y: (zonesPixBox.yMin + zonesPixBox.yMax) / 2
    }

    let scale = getOptimalScale(this, zonesPixBox, {minScale, maxScale, marginRatio})

    if (snap) {
        scale = Math.round(scale)
    }

    return {
        center: this.pixelCoordsToCoords(pixCenter),
        scale
    }

}


// zones : array of {x:, y:, margin:} (margin is optional)
Screen.prototype.setOptimalCamera = function (zones, params = {}) {
    let {center, scale} = this.optimalCameraParamsFor(zones, params)

    if (params.intScale) {
        scale = Math.floor(scale)
    }

    this.setCamera({center, scale})

    if (params.snap) {
        let {x, y} = this.coordsToPixelCoords({x: 0, y: 0})
        center.x -= (x % 1) / scale
        center.y -= (y % 1) / scale
        this.setCenter(center)
    }

    return {center, scale}
}


function getOptimalScale (screen, {xMin, xMax, yMin, yMax}, {minScale, maxScale, marginRatio = 1}) {

    let shrinkFactorX = (xMax - xMin) / screen.width
    let shrinkFactorY = (yMax - yMin) / screen.height

    let shrinkFactor  = Math.max(shrinkFactorX, shrinkFactorY) * marginRatio

    if (shrinkFactor > 0) {
        let newScale = screen.scale / shrinkFactor

        if (typeof minScale !== "undefined") {
            newScale = Math.max(minScale, newScale)
        }

        if (typeof maxScale !== "undefined") {
            newScale = Math.min(maxScale, newScale)
        }

        return newScale
    } else {
        return screen.scale   //FIXME which case is covered by this ?
    }
}


function addZoneToPixBox (zonesPixBox, {x, y, margin}) {
    zonesPixBox.xMin = Math.min(x - margin, zonesPixBox.xMin)
    zonesPixBox.xMax = Math.max(x + margin, zonesPixBox.xMax)
    zonesPixBox.yMin = Math.min(y - margin, zonesPixBox.yMin)
    zonesPixBox.yMax = Math.max(y + margin, zonesPixBox.yMax)
}












//****************************** BOUNDING BOX AND TILING

Screen.prototype.boundingBox             = boundingBoxWithOrientation
Screen.prototype.boundingBoxForWholeView = boundingBoxWithOrientation //FIXME deprecate ?

Screen.prototype.pixBoundingBox = function ({topLeft, width, height, orientation, relativeTo}) {
    topLeft = this.coordsToPixelCoords(topLeft, relativeTo)

    if (relativeTo) {
        if ("globalOrientation" in relativeTo) {
            orientation = relativeTo.globalOrientation(orientation)
            width       = relativeTo.globalLength(width)
            height      = relativeTo.globalLength(height)
        } else {
            let {orientation: referenceFrameOrientation = 0, scale = 1} = relativeTo
            orientation += referenceFrameOrientation
            width  *= scale
            height *= scale
        }
    }

    orientation += this.orientation
    width  *= this.scale
    height *= this.scale

    return this.boundingBox({
        width,
        height,
        topLeft,
        orientation,
        yInversion: true
    })
}


Screen.prototype.boundingBoxForPixelZone = function ({topLeft, width, height}) {
    topLeft = this.pixelCoordsToCoords(topLeft)
    width  /= this.scale
    height /= this.scale

    return this.boundingBox({
        topLeft,
        width,
        height,
        orientation: -this.orientation,
        yInversion:  this.yDir === -1
    })
}


Screen.prototype.forEachVisibleTile = function ({
    tileWidth,
    tileHeight = tileWidth,
    center = {x: 0, y: 0},
    iterator
}) {

    if (typeof tileHeight === "undefined") {
        tileHeight = 1
    }
    if (typeof tileWidth === "undefined") {
        tileWidth = tileHeight
    }

    let {xMin, xMax, yMin, yMax} = this.boundingBox()

    let semiWidth  = tileWidth  / 2
    let semiHeight = tileHeight / 2

    xMin = tileWidth  * Math.floor((xMin - center.x) / tileWidth)     + center.x
    xMax = tileWidth  * Math.ceil((xMax - center.x) / tileWidth)      + center.x
    yMin = tileHeight * Math.ceil((yMin - center.y) / tileHeight)     + center.y
    yMax = tileHeight * Math.ceil(1 + (yMax - center.y) / tileHeight) + center.y

    for (let x = xMin; x < xMax; x += tileWidth) {
        for (let y = yMin; y < yMax; y += tileHeight) {
            iterator({
                xTile: Math.round((x - center.x) / tileWidth),
                yTile: Math.round((y - center.y) / tileHeight),
                x,
                y,
                xMin: x,
                yMin: y,
                xMax: x + tileWidth,
                yMax: y + tileHeight,
                width:  tileWidth,
                height: tileHeight,
                topLeft: {x, y},
                center:  {
                    x: x + semiWidth,
                    y: y - semiHeight
                }
            })
        }
    }
}




function paramsForBoundingBoxForWholeView (screen) {
    return {
        topLeft: screen.pixelCoordsToCoords({x: 0, y: 0}),
        width:   screen.width  / screen.scale,
        height:  screen.height / screen.scale,
        orientation: -screen.orientation,
        yInversion:   screen.yDir === -1
    }
}


function boundingBoxWithOrientation (params) {

    let {topLeft, width, height, orientation, yInversion} = params || paramsForBoundingBoxForWholeView(this)
    let {x, y}      = topLeft
    let yMultiplier = yInversion ? -1 : 1

    if (!orientation) {
        let yMin = y - yMultiplier * height
        return {
            x,
            y,
            topLeft: {x, y},
            center: {
                x: x    + width  / 2,
                y: yMin + height / 2
            },
            unitInPixels: this.scale,
            scale:        this.scale,  //deprecated alias of unitInPixels
            width,
            height,
            xMin: x,
            yMax: y,
            xMax: x + width,
            yMin
        }
    }



    let orientationRad = orientation * degreesToRadians
    let cos          = Math.cos(orientationRad)
    let sin          = Math.sin(orientationRad)

    let topRightX    = x         + cos * width
    let bottomRightX = topRightX + sin * height
    let bottomLeftX  = x         + sin * height

    let xMin         = Math.min(x, topRightX, bottomRightX, bottomLeftX)
    let xMax         = Math.max(x, topRightX, bottomRightX, bottomLeftX)

    let topRightY    = y         + yMultiplier * sin * width
    let bottomRightY = topRightY - yMultiplier * cos * height
    let bottomLeftY  = y         - yMultiplier * cos * height
    let yMin         = Math.min(y, topRightY, bottomRightY, bottomLeftY)
    let yMax         = Math.max(y, topRightY, bottomRightY, bottomLeftY)

    let topY = (yInversion ? yMin : yMax)

    width  = xMax - xMin
    height = yMax - yMin

    return {
        x: xMin,
        y: topY,
        topLeft: {x: xMin, y: topY},
        center:  {
            x: xMin + width  / 2,
            y: yMin + height / 2
        },
        scale: this.scale,
        width,
        height,
        xMin,
        yMin,
        xMax,
        yMax
    }
}







export default Screen

